// src/services/db.js
import { sendEmail } from "./notify";

/*  KEYS  */
const KEYS = Object.freeze({
  USER: "user",
  REQ: "baranja",
  SETTINGS: "appSettings",
  DICTS: "dictionaries",
  CALLS: "calls", // телефонски повици
  CLIENTS: "korisnici", //  клиенти / фирми
});

/* Helpers  */
function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (err) {
    console.warn(` Грешка при читање ${key}:`, err);
    return fallback;
  }
}

function write(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (err) {
    console.error(`[ Неуспешно запишување на ${key}:`, err);
  }
}

/* Корисник */
export const getUser = () => read(KEYS.USER, null);
export const setUser = (u) => write(KEYS.USER, u);
export const clearUser = () => localStorage.removeItem(KEYS.USER);

/*  Подесувања  */
export function getSettings() {
  const def = {
    currentYear: new Date().getFullYear(),
    allowRegistration: true,
    systemOperatorEmail: "admin@megasoft.com",
  };
  const s = read(KEYS.SETTINGS, def);
  if (!s.currentYear) s.currentYear = def.currentYear;
  write(KEYS.SETTINGS, s);
  return s;
}

export function updateSettings(patch) {
  const next = { ...getSettings(), ...patch };
  write(KEYS.SETTINGS, next);
  return next;
}

/*   (Sifrarnici)  */
export function getDictionaries() {
  const def = {
    serviceTypes: ["Инсталација", "Одржување", "Техничка поддршка"],
    statuses: [
      "Отворено",
      "Во тек",
      "Во обработка",
      "Одобрено",
      "Одбиено",
      "Завршено",
      "Откажано",
    ],
    roles: ["Клиент", "Вработен", "СистемОператор"],
    employees: ["vraboten@megasoft.com", "vraboten2@megasoft.com"],
  };

  const d = read(KEYS.DICTS, def);
  Object.keys(def).forEach((k) => {
    if (!Array.isArray(d[k])) d[k] = def[k];
  });
  write(KEYS.DICTS, d);
  return d;
}

export const updateDictionaries = (patch) => {
  const next = { ...getDictionaries(), ...patch };
  write(KEYS.DICTS, next);
  return next;
};

/*  Барања */
function ensureStore() {
  const arr = read(KEYS.REQ, []);
  if (!Array.isArray(arr)) {
    write(KEYS.REQ, []);
    return [];
  }
  return arr;
}

export const getAllBaranja = () => ensureStore();
export const saveAllBaranja = (list) => write(KEYS.REQ, list);
export const writeAll = (list) => write(KEYS.REQ, list); 

export function nextNumberForYear(year) {
  const all = getAllBaranja().filter((b) => b.year === year);
  const max = all.reduce((m, b) => Math.max(m, b.number || 0), 0);
  return max + 1;
}

export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").toLowerCase());

export const isDateInCurrentYear = (isoDate, year) =>
  !!isoDate && Number(String(isoDate).slice(0, 4)) === Number(year);

export function clampDateToYear(isoDate, year) {
  const y = Number(year);
  const date = new Date(isoDate || Date.now());
  if (date.getFullYear() !== y) {
    const today = new Date();
    return `${y}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`;
  }
  return isoDate;
}

/*  Додавање ново барање  */
export function addBaranje({ tip, naslov, opis, datum, clientEmail }) {
  const settings = getSettings();
  const store = getAllBaranja();
  const id = Date.now();
  const year = settings.currentYear;
  const number = nextNumberForYear(year);
  const d = clampDateToYear(
    datum || new Date().toISOString().slice(0, 10),
    year
  );

  const rec = {
    id,
    year,
    number,
    tip: tip || "",
    naslov: naslov || "",
    opis: opis || "",
    datum: d,
    status: "Во обработка",
    createdAt: new Date().toISOString(),
    clientEmail: clientEmail || "",
    assignedTo: null,
    hours: 0,
    notes: "",
    closedAt: null,
  };

  store.push(rec);
  saveAllBaranja(store);

  if (isValidEmail(rec.clientEmail)) {
    sendEmail({
      to: rec.clientEmail,
      subject: `Барање ${rec.year}/${String(rec.number).padStart(
        3,
        "0"
      )} е отворено`,
      html: `
        <div>Вашето барање е успешно отворено.</div>
        <div><b>Број:</b> ${rec.year}/${String(rec.number).padStart(
        3,
        "0"
      )}</div>
        <div><b>Наслов/Тип:</b> ${rec.naslov || rec.tip || ""}</div>
        <div><b>Датум:</b> ${rec.datum}</div>
      `,
    });
  }

  return rec;
}

/* Уредување и финализирање  */
export function updateBaranje(idOrBroj, patch) {
  const store = getAllBaranja();
  const idx = store.findIndex(
    (b) =>
      b.id === idOrBroj ||
      `${b.year}/${b.number}` === idOrBroj ||
      b.broj === idOrBroj
  );
  if (idx === -1) return null;

  const prev = store[idx];
  const next = { ...prev, ...patch };

  if (next.status === "Завршено" && !next.closedAt) {
    next.closedAt = new Date().toISOString();
  }

  store[idx] = next;
  saveAllBaranja(store);
  return next;
}

export function finalizeBaranje(idOrBroj, { qNotify } = {}) {
  const store = getAllBaranja();
  const idx = store.findIndex(
    (b) =>
      b.id === idOrBroj ||
      `${b.year}/${b.number}` === idOrBroj ||
      b.broj === idOrBroj
  );
  if (idx === -1) return null;

  const item = { ...store[idx] };
  item.status = "Завршено";
  if (!item.closedAt) item.closedAt = new Date().toISOString();

  store[idx] = item;
  saveAllBaranja(store);

  if (isValidEmail(item.clientEmail)) {
    sendEmail(
      {
        to: item.clientEmail,
        subject: `Барање ${item.year}/${String(item.number).padStart(
          3,
          "0"
        )} - Завршено`,
        html: `
          <div>Вашето барање е завршено.</div>
          <div><b>Број:</b> ${item.year}/${String(item.number).padStart(
          3,
          "0"
        )}</div>
          <div><b>Завршено на:</b> ${item.closedAt}</div>
          <div><b>Белешка:</b> ${item.notes || ""}</div>
        `,
      },
      qNotify
    );
  }
  return item;
}

/*  Филтрирање  */
export function filterBaranja({
  from,
  to,
  status,
  employee,
  clientEmail,
} = {}) {
  const list = getAllBaranja();
  return list.filter((b) => {
    if (clientEmail && b.clientEmail !== clientEmail) return false;
    if (employee === "нераспределени" && b.assignedTo) return false;
    if (
      employee &&
      employee !== "нераспределени" &&
      employee !== "Сите" &&
      b.assignedTo !== employee
    )
      return false;
    if (status && status !== "Сите" && b.status !== status) return false;
    if (from && b.datum < from) return false;
    if (to && b.datum > to) return false;
    return true;
  });
}

/*  CSV Export  */
export function toCSV(rows, columns) {
  const head = columns.map((c) => `"${c.label}"`).join(";");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = typeof c.field === "function" ? c.field(r) : r[c.field];
          return `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
        })
        .join(";")
    )
    .join("\n");
  return head + "\n" + body;
}

export function downloadCSV(filename, csvString) {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/*  Телефонски повици  */
function readCalls() {
  try {
    return JSON.parse(localStorage.getItem(KEYS.CALLS) || "[]");
  } catch {
    return [];
  }
}
function writeCalls(list) {
  localStorage.setItem(KEYS.CALLS, JSON.stringify(list));
}

export function getCalls() {
  return readCalls();
}
export function addCall(call) {
  const list = readCalls();
  const id = call.id || "CALL-" + Date.now();
  list.push({ ...call, id });
  writeCalls(list);
  return id;
}
export function updateCall(id, patch) {
  const list = readCalls();
  const i = list.findIndex((c) => c.id === id);
  if (i >= 0) {
    list[i] = { ...list[i], ...patch };
    writeCalls(list);
  }
}
export function closeCallWithoutOrder(id) {
  updateCall(id, { status: "Затворено без налог" });
}
export function createBaranjeFromCall(call) {
  const baranja = getAllBaranja();
  const settings = getSettings();
  const year = settings.currentYear;
  const number = nextNumberForYear(year);

  const id = `${year}-${String(number).padStart(4, "0")}`;
  const novo = {
    id,
    year,
    number,
    datum: new Date().toISOString().slice(0, 10),
    naslov: `Телефонски повик од ${
      call.callerName || call.callerPhone || "непознат"
    }`,
    opis: call.note || "Креирано од телефонски повик.",
    tip: "Телефонски повик",
    status: "Во обработка",
    assignedTo: call.assignedTo || "",
    hours: 0,
    notes: "",
    clientEmail: "",
  };

  baranja.push(novo);
  saveAllBaranja(baranja);
  updateCall(call.id, { status: "Отворено налог", baranjeId: id });
  return id;
}

/*  Клиенти  */
function readClients() {
  try {
    return JSON.parse(localStorage.getItem(KEYS.CLIENTS) || "[]");
  } catch {
    return [];
  }
}
function writeClients(list) {
  localStorage.setItem(KEYS.CLIENTS, JSON.stringify(list));
}

export function getClients() {
  return readClients();
}
export function addClient(client) {
  const list = readClients();
  const id = client.id || "CLIENT-" + Date.now();
  list.push({
    id,
    ime: client.ime || "",
    email: client.email || "",
    telefon: client.telefon || "",
    dogovorDo: client.dogovorDo || "2050-12-31",
    saatiOdrzuvanje: Number(client.saatiOdrzuvanje) || 0,
    saatiIzmeni: Number(client.saatiIzmeni) || 0,
  });
  writeClients(list);
  return id;
}
export function updateClient(id, patch) {
  const list = readClients();
  const i = list.findIndex((c) => c.id === id);
  if (i >= 0) {
    list[i] = { ...list[i], ...patch };
    writeClients(list);
  }
}
export function deleteClient(id) {
  const list = readClients().filter((c) => c.id !== id);
  writeClients(list);
}

/*  Наоѓање барање по ID  */
export function getBaranjeById(idOrBroj) {
  const all = getAllBaranja();
  return (
    all.find(
      (b) =>
        b.id === idOrBroj ||
        `${b.year}/${b.number}` === idOrBroj ||
        b.broj === idOrBroj
    ) || null
  );
}
